import React, { useState } from 'react'
import { useSWRConfig } from 'swr'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/use-toast'
import { Api, type SyncFromBitablePreviewItem } from './api'

/** 与 server feishu-approval.constants FEISHU_PAINTING_BITABLE 一致 */
const BITABLE_URL =
  'https://stickerstyle.feishu.cn/base/DY9qbtOI8adzlPsKunVcZwzQn6c?table=tblolxdDy4lyLtrq'

type Step = 'input' | 'confirm'

const firstLinePreview = (text: string, max = 10) => {
  const line = (text || '').split(/\r?\n/)[0]?.trim() || ''
  if (!line) return '（无描述）'
  return line.length > max ? line.slice(0, max) : line
}

const SyncFromBitableDialog: React.FC = () => {
  const { mutate } = useSWRConfig()
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<Step>('input')
  const [week, setWeek] = useState('')
  const [previewItems, setPreviewItems] = useState<
    SyncFromBitablePreviewItem[]
  >([])
  const [loading, setLoading] = useState(false)
  const [progressText, setProgressText] = useState('')

  const reset = () => {
    setStep('input')
    setWeek('')
    setPreviewItems([])
    setLoading(false)
    setProgressText('')
  }

  const refreshList = () =>
    mutate(
      (key) => Array.isArray(key) && key[0] === '/painting-requests',
      undefined,
      { revalidate: true },
    )

  const onFetch = async () => {
    const w = week.trim()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(w)) {
      toast({
        variant: 'destructive',
        title: '周次格式不正确',
        description: '请输入如 2026-08-02',
      })
      return
    }
    try {
      setLoading(true)
      setProgressText('')
      const res = await Api.previewFromBitable(w)
      if (!res?.items?.length) {
        toast({
          variant: 'destructive',
          title: '未找到需求',
          description: `周次 ${w} 下没有可同步的行`,
        })
        return
      }
      setPreviewItems(res.items)
      setStep('confirm')
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: '读取多维表格失败',
        description: err?.message || '未知错误',
      })
    } finally {
      setLoading(false)
    }
  }

  const onCreate = async () => {
    const w = week.trim()
    if (!w || !previewItems.length) return
    try {
      setLoading(true)
      setProgressText('正在下载图片…')
      const synced = await Api.syncFromBitable(w)
      const drafts = synced?.items || []
      if (!drafts.length) {
        toast({
          variant: 'destructive',
          title: '没有可创建的数据',
        })
        return
      }

      let ok = 0
      const errors: string[] = []
      for (let i = 0; i < drafts.length; i++) {
        const d = drafts[i]
        setProgressText(`正在新建 ${i + 1}/${drafts.length}…`)
        if (!d.styleDescription?.trim()) {
          errors.push(`${d.weekNo}：缺少需求描述，已跳过`)
          continue
        }
        try {
          await Api.create({
            styleDescription: d.styleDescription.trim(),
            styleReferenceImages: d.styleReferenceImages || [],
            desc: (d.desc || d.weekNo || '').trim(),
          })
          ok += 1
        } catch (err: any) {
          errors.push(`${d.weekNo}：${err?.message || '未知错误'}`)
        }
      }

      if (ok > 0) {
        toast({
          title: `已新建 ${ok}/${drafts.length} 条`,
          className: 'bg-green-500 text-white',
        })
        await refreshList()
      }
      if (errors.length) {
        toast({
          variant: 'destructive',
          title: '部分创建失败',
          description: errors.slice(0, 5).join('；'),
        })
      }
      if (ok === drafts.length) {
        setOpen(false)
        reset()
      }
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: '导入失败',
        description: err?.message || '未知错误',
      })
    } finally {
      setLoading(false)
      setProgressText('')
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) reset()
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">从多维表格同步</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>从多维表格同步</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              <p>
                {step === 'input'
                  ? '输入年-月-周，例如 2026-08-02。第一步只查有多少条，不会下载图片。'
                  : `已找到 ${previewItems.length} 条需求。确认后才会下载图片并逐条新建。`}
              </p>
              <p>
                多维表格：
                <a
                  href={BITABLE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-primary hover:underline"
                >
                  {BITABLE_URL}
                </a>
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {step === 'input' ? (
          <div className="space-y-2 py-2">
            <Label>年-月-周</Label>
            <Input
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              placeholder="2026-08-02"
              disabled={loading}
            />
          </div>
        ) : (
          <div className="space-y-2 py-2">
            <div className="max-h-56 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <tbody>
                  {previewItems.map((d) => (
                    <tr
                      key={d.recordId}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="px-3 py-2 align-middle">
                        {firstLinePreview(d.styleDescription)}
                      </td>
                      <td className="w-[72px] px-3 py-2 text-right align-middle text-muted-foreground">
                        详情 {d.detailImageCount}
                      </td>
                      <td className="w-[72px] px-3 py-2 text-right align-middle text-muted-foreground">
                        参考 {d.refImageCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {progressText ? (
              <p className="text-xs text-muted-foreground">{progressText}</p>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {step === 'confirm' ? (
            <Button
              variant="outline"
              onClick={() => setStep('input')}
              disabled={loading}
            >
              返回
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setOpen(false)}>
              取消
            </Button>
          )}
          {step === 'input' ? (
            <Button onClick={onFetch} disabled={loading}>
              {loading ? '查询中...' : '确定'}
            </Button>
          ) : (
            <Button onClick={onCreate} disabled={loading}>
              {loading
                ? progressText || '处理中...'
                : `确认新建（${previewItems.length}）`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SyncFromBitableDialog
